import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CtaButton } from "@/components/site/CtaButton";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isoDate = (d) => d.toISOString().slice(0, 10);
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d; };
const plus30 = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d; };

const CLINIC_TZ = "Europe/Paris";
const tzOffsetHours = (tz) => {
  try {
    const s = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" }).format(new Date());
    const m = s.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!m) return 0;
    return (m[1] === "-" ? -1 : 1) * (parseInt(m[2], 10) + parseInt(m[3], 10) / 60);
  } catch {
    return 0;
  }
};
const fmtGmt = (o) => `GMT${o >= 0 ? "+" : "-"}${Math.abs(o)}`;

const TzNote = () => {
  const visitorTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const clinicOff = tzOffsetHours(CLINIC_TZ);
  const visitorOff = tzOffsetHours(visitorTz);
  const diff = Math.round((visitorOff - clinicOff) * 2) / 2;
  return (
    <p className="mt-3 text-sm text-[#5c5c58] leading-relaxed" data-testid="booking-tz-note">
      {diff === 0
        ? `All times are your local time — ${CLINIC_TZ} (${fmtGmt(clinicOff)}).`
        : `All times are Paris time (${fmtGmt(clinicOff)}). Your device: ${visitorTz} (${fmtGmt(visitorOff)}) — ${Math.abs(diff)}h ${diff < 0 ? "behind" : "ahead of"} Paris.`}
    </p>
  );
};

const inputCls =
  "w-full bg-transparent border-b border-white/15 py-3 text-[#f5f3ee] text-lg placeholder:text-[#5c5c58] focus:border-[#5CCBF5] focus:outline-none transition-colors duration-300";

const BookingForm = ({ sessionId }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState(isoDate(tomorrow()));
  const [time, setTime] = useState("");
  const [taken, setTaken] = useState([]);
  const [slots, setSlots] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error
  const [error, setError] = useState("");
  const [booked, setBooked] = useState(null);
  const [wasExisting, setWasExisting] = useState(false);

  useEffect(() => {
    if (!date) return;
    setTime("");
    axios.get(`${API}/bookings/availability`, { params: { date } })
      .then(({ data }) => { setTaken(data.taken); setSlots(data.slots); })
      .catch(() => { setTaken([]); setSlots([]); });
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Your name and a valid email, please.");
      return;
    }
    if (!date || !time) {
      setError("Pick a date and a time slot.");
      return;
    }
    setStatus("submitting");
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        session_id: sessionId, name: name.trim(), email, date, time,
      });
      setWasExisting(data.date !== date || data.time !== time);
      setBooked(data);
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "done" && booked) {
    return (
      <div className="mt-12 border border-[#5CCBF5]/40 bg-[#131C31] p-8 max-w-2xl" data-testid="booking-success">
        <p className="eyebrow mb-3">Appointment Booked</p>
        <h2 className="display text-2xl sm:text-3xl text-[#f5f3ee]">
          See you on {booked.date} at {booked.time} (Paris time).
        </h2>
        <p className="mt-4 text-[#a7a7a2] leading-relaxed">
          A confirmation is on its way to {booked.email}. Before the call, jot down what a normal
          week actually looks like — the messier and more honest, the more I'll find.
        </p>
        {wasExisting && (
          <p className="mt-4 text-sm text-[#5CCBF5]" data-testid="booking-existing-note">
            You already had a call booked — this is your existing slot. Reply to your confirmation
            email if you need to move it.
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-12 border border-white/10 bg-[#131C31] p-8 max-w-2xl" data-testid="booking-form">
      <p className="eyebrow mb-3">One Last Step</p>
      <h2 className="display text-2xl sm:text-3xl text-[#f5f3ee]">Book your Checkup call.</h2>
      <p className="mt-3 text-[#a7a7a2] leading-relaxed">45–60 minutes. Pick a slot that suits you.</p>

      <div className="mt-8 grid sm:grid-cols-2 gap-8">
        <div>
          <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#a7a7a2] block mb-3">Your name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className={inputCls} data-testid="booking-name-input" />
        </div>
        <div>
          <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#a7a7a2] block mb-3">Your email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@business.com" className={inputCls} data-testid="booking-email-input" />
        </div>
      </div>

      <div className="mt-8">
        <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#a7a7a2] block mb-3">Pick a day</label>
        <input
          type="date"
          value={date}
          min={isoDate(tomorrow())}
          max={isoDate(plus30())}
          onChange={(e) => setDate(e.target.value)}
          className={`${inputCls} [color-scheme:dark]`}
          data-testid="booking-date-input"
        />
      </div>

      <div className="mt-8">
        <label className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#a7a7a2] block mb-3">Pick a time</label>
        <TzNote />
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="booking-slots">
          {slots.map((s) => {
            const isTaken = taken.includes(s);
            const isSelected = time === s;
            return (
              <button
                key={s}
                type="button"
                disabled={isTaken}
                onClick={() => setTime(s)}
                data-testid={`booking-slot-${s.replace(":", "")}`}
                className={`py-3 font-mono text-[13px] tracking-[0.1em] border transition-colors duration-300 ${
                  isTaken
                    ? "border-white/5 text-[#5c5c58] line-through cursor-not-allowed"
                    : isSelected
                      ? "border-[#5CCBF5] bg-[#5CCBF5] text-[#0E1526]"
                      : "border-white/15 text-[#f5f3ee] hover:border-[#5CCBF5] hover:text-[#5CCBF5]"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-10">
        <CtaButton
          onClick={submit}
          testid="booking-submit-button"
          className={status === "submitting" ? "opacity-60 pointer-events-none" : ""}
        >
          {status === "submitting" ? "Booking…" : "Confirm My Slot"}
        </CtaButton>
      </div>
      {error && <p className="mt-4 text-sm text-red-400" data-testid="booking-error">{error}</p>}
    </form>
  );
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | unpaid | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 15) {
          setState("unpaid");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (err) {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page">
      <Seo title="Payment Confirmed | The AI Doctor" description="Your checkup is confirmed — one last step: book your call." />
      <section className="shell min-h-screen flex flex-col justify-center py-40">
        <span className="eyebrow">Payment</span>
        {state === "checking" && (
          <div data-testid="payment-checking">
            <h1 className="mt-6 text-4xl sm:text-5xl font-medium text-[#f5f3ee]">Confirming your payment…</h1>
            <p className="mt-6 text-base text-[#a7a7a2]">One moment while I check with the front desk.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-medium text-[#f5f3ee] max-w-3xl">
              You're on the books.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#a7a7a2] max-w-xl leading-relaxed">
              Payment received. Your AI Efficiency Checkup is confirmed — a confirmation email is on
              its way to your inbox.
            </p>
            <BookingForm sessionId={sessionId} />
            <div className="mt-10">
              <CtaButton to="/" variant="ghost" testid="payment-home-button">Back to the clinic</CtaButton>
            </div>
          </div>
        )}
        {(state === "unpaid" || state === "error") && (
          <div data-testid="payment-unconfirmed">
            <h1 className="mt-6 text-4xl sm:text-5xl font-medium text-[#f5f3ee] max-w-3xl">
              That payment couldn't be confirmed.
            </h1>
            <p className="mt-6 text-base text-[#a7a7a2] max-w-xl leading-relaxed">
              If your card was charged, don't worry — it will settle or refund automatically.
              Otherwise, you can try again.
            </p>
            <div className="mt-10">
              <CtaButton to="/book-checkup" testid="payment-retry-button">Try again</CtaButton>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
